/**
 * Dissociation parents — central table mapping a neutral molecular
 * compound to the set of ions it produces in solution (session 340).
 *
 * Used by:
 *   - VesselInfoPanel inspector — to group dissolved-ion rows under a
 *     «من <parent>» dissociation equation so the student reads the
 *     balanced dissolution rather than bare ions.
 *   - Stage.tsx bottle label regenerator — so a bottle whose mixture
 *     is stored as ions (e.g. H+ + SO4(2-)) still shows
 *     «حمض الكبريتيك / H2SO4» on its physical label, plus the
 *     correct strength + corrosivity warning.
 *
 * For acids and bases the entry also carries a `class` ("acid" |
 * "base"), `strength` ("strong" | "weak" | "weak-dangerous"), an
 * Arabic display name, and a hazard label. These drive the bottle
 * label warning banner («حمض قوي / مادة أكّالة») and the inspector
 * hazard chips.
 *
 * Real chemistry only — entries match species the lab actually carries
 * (cross-referenced with src/domains/chemistry/parts/glassware/).
 */
export const DISSOCIATION_PARENTS = [
    // Strong acids — full dissociation in water, all corrosive.
    {
        parent: "H2SO4", ions: [["H+", 2], ["SO4(2-)", 1]],
        class: "acid", strength: "strong",
        arName: "حمض الكبريتيك", warningAr: "حمض قوي / مادة أكّالة"
    },
    {
        parent: "HCl", ions: [["H+", 1], ["Cl-", 1]],
        class: "acid", strength: "strong",
        arName: "حمض الهيدروكلوريك", warningAr: "حمض قوي / مادة أكّالة"
    },
    {
        parent: "HNO3", ions: [["H+", 1], ["NO3-", 1]],
        class: "acid", strength: "strong",
        arName: "حمض النيتريك", warningAr: "حمض قوي / مادة أكّالة"
    },
    {
        parent: "HBr", ions: [["H+", 1], ["Br-", 1]],
        class: "acid", strength: "strong",
        arName: "حمض الهيدروبروميك", warningAr: "حمض قوي / مادة أكّالة"
    },
    {
        parent: "HI", ions: [["H+", 1], ["I-", 1]],
        class: "acid", strength: "strong",
        arName: "حمض الهيدرويوديك", warningAr: "حمض قوي / مادة أكّالة"
    },
    {
        parent: "HClO4", ions: [["H+", 1], ["ClO4-", 1]],
        class: "acid", strength: "strong",
        arName: "حمض البيركلوريك", warningAr: "حمض قوي / مؤكسد قوي / مادة أكّالة"
    },
    // Weak acids — partial dissociation. Ka small but still corrosive
    // at lab concentrations.
    {
        parent: "CH3COOH", ions: [["H+", 1], ["CH3COO-", 1]],
        class: "acid", strength: "weak",
        arName: "حمض الخل", warningAr: "حمض ضعيف / مهيج"
    },
    {
        parent: "HCOOH", ions: [["H+", 1], ["HCOO-", 1]],
        class: "acid", strength: "weak",
        arName: "حمض الفورميك", warningAr: "حمض ضعيف / أكّال"
    },
    {
        parent: "H2CO3", ions: [["H+", 2], ["CO3(2-)", 1]],
        class: "acid", strength: "weak",
        arName: "حمض الكربونيك", warningAr: "حمض ضعيف"
    },
    {
        parent: "H3PO4", ions: [["H+", 3], ["PO4(3-)", 1]],
        class: "acid", strength: "weak",
        arName: "حمض الفوسفوريك", warningAr: "حمض ضعيف / مهيج"
    },
    // HF is technically weak by Ka but DEEPLY dangerous (bone-binding).
    {
        parent: "HF", ions: [["H+", 1], ["F-", 1]],
        class: "acid", strength: "weak-dangerous",
        arName: "حمض الهيدروفلوريك", warningAr: "حمض ضعيف لكنه خطر جدا / يخترق الجلد"
    },
    // Strong bases — full dissociation, corrosive.
    {
        parent: "NaOH", ions: [["Na+", 1], ["OH-", 1]],
        class: "base", strength: "strong",
        arName: "هيدروكسيد الصوديوم", warningAr: "قاعدة قوية / مادة أكّالة"
    },
    {
        parent: "KOH", ions: [["K+", 1], ["OH-", 1]],
        class: "base", strength: "strong",
        arName: "هيدروكسيد البوتاسيوم", warningAr: "قاعدة قوية / مادة أكّالة"
    },
    {
        parent: "LiOH", ions: [["Li+", 1], ["OH-", 1]],
        class: "base", strength: "strong",
        arName: "هيدروكسيد الليثيوم", warningAr: "قاعدة قوية / مادة أكّالة"
    },
    {
        parent: "Ca(OH)2", ions: [["Ca2+", 1], ["OH-", 2]],
        class: "base", strength: "strong",
        arName: "هيدروكسيد الكالسيوم", warningAr: "قاعدة قوية / مهيج"
    },
    {
        parent: "Ba(OH)2", ions: [["Ba2+", 1], ["OH-", 2]],
        class: "base", strength: "strong",
        arName: "هيدروكسيد الباريوم", warningAr: "قاعدة قوية / سامة"
    },
    // Weak base — NH3 ⇌ NH4+ + OH-. Inspector groups it when both
    // NH4+ and OH- are present in dominant amounts; the bottle label
    // for the NH3 reagent stays «أمونيا».
    {
        parent: "NH3", ions: [["NH4+", 1], ["OH-", 1]],
        class: "base", strength: "weak",
        arName: "الامونيا", warningAr: "قاعدة ضعيفة / مهيج"
    },
    // Salts — soluble strong electrolytes. No strength/warning fields:
    // these are inspector-grouping aids only.
    { parent: "NaCl", ions: [["Na+", 1], ["Cl-", 1]], class: "salt" },
    { parent: "NaBr", ions: [["Na+", 1], ["Br-", 1]], class: "salt" },
    { parent: "NaI", ions: [["Na+", 1], ["I-", 1]], class: "salt" },
    { parent: "NaNO3", ions: [["Na+", 1], ["NO3-", 1]], class: "salt" },
    { parent: "Na2SO4", ions: [["Na+", 2], ["SO4(2-)", 1]], class: "salt" },
    { parent: "Na2CO3", ions: [["Na+", 2], ["CO3(2-)", 1]], class: "salt" },
    { parent: "NaHCO3", ions: [["Na+", 1], ["HCO3-", 1]], class: "salt" },
    { parent: "Na2SiO3", ions: [["Na+", 2], ["SiO3(2-)", 1]], class: "salt" },
    { parent: "Na2S2O3", ions: [["Na+", 2], ["S2O3(2-)", 1]], class: "salt" },
    { parent: "CH3COONa", ions: [["Na+", 1], ["CH3COO-", 1]], class: "salt" },
    { parent: "Na2S", ions: [["Na+", 2], ["S2-", 1]], class: "salt" },
    { parent: "KCl", ions: [["K+", 1], ["Cl-", 1]], class: "salt" },
    { parent: "KBr", ions: [["K+", 1], ["Br-", 1]], class: "salt" },
    { parent: "KI", ions: [["K+", 1], ["I-", 1]], class: "salt" },
    { parent: "KNO3", ions: [["K+", 1], ["NO3-", 1]], class: "salt" },
    { parent: "K2SO4", ions: [["K+", 2], ["SO4(2-)", 1]], class: "salt" },
    { parent: "K2CrO4", ions: [["K+", 2], ["CrO4(2-)", 1]], class: "salt" },
    { parent: "K2Cr2O7", ions: [["K+", 2], ["Cr2O7(2-)", 1]], class: "salt" },
    { parent: "KMnO4", ions: [["K+", 1], ["MnO4-", 1]], class: "salt" },
    { parent: "KSCN", ions: [["K+", 1], ["SCN-", 1]], class: "salt" },
    { parent: "KClO3", ions: [["K+", 1], ["ClO3-", 1]], class: "salt" },
    { parent: "NH4NO3", ions: [["NH4+", 1], ["NO3-", 1]], class: "salt" },
    { parent: "NH4Cl", ions: [["NH4+", 1], ["Cl-", 1]], class: "salt" },
    { parent: "(NH4)2SO4", ions: [["NH4+", 2], ["SO4(2-)", 1]], class: "salt" },
    { parent: "AgNO3", ions: [["Ag+", 1], ["NO3-", 1]], class: "salt" },
    { parent: "CuSO4", ions: [["Cu2+", 1], ["SO4(2-)", 1]], class: "salt" },
    { parent: "CuCl2", ions: [["Cu2+", 1], ["Cl-", 2]], class: "salt" },
    { parent: "Cu(NO3)2", ions: [["Cu2+", 1], ["NO3-", 2]], class: "salt" },
    { parent: "FeCl2", ions: [["Fe2+", 1], ["Cl-", 2]], class: "salt" },
    { parent: "FeCl3", ions: [["Fe3+", 1], ["Cl-", 3]], class: "salt" },
    { parent: "FeSO4", ions: [["Fe2+", 1], ["SO4(2-)", 1]], class: "salt" },
    { parent: "MgCl2", ions: [["Mg2+", 1], ["Cl-", 2]], class: "salt" },
    { parent: "MgSO4", ions: [["Mg2+", 1], ["SO4(2-)", 1]], class: "salt" },
    { parent: "CaCl2", ions: [["Ca2+", 1], ["Cl-", 2]], class: "salt" },
    { parent: "Ca(NO3)2", ions: [["Ca2+", 1], ["NO3-", 2]], class: "salt" },
    { parent: "BaCl2", ions: [["Ba2+", 1], ["Cl-", 2]], class: "salt" },
    { parent: "Ba(NO3)2", ions: [["Ba2+", 1], ["NO3-", 2]], class: "salt" },
    { parent: "ZnCl2", ions: [["Zn2+", 1], ["Cl-", 2]], class: "salt" },
    { parent: "ZnSO4", ions: [["Zn2+", 1], ["SO4(2-)", 1]], class: "salt" },
    { parent: "AlCl3", ions: [["Al3+", 1], ["Cl-", 3]], class: "salt" },
    { parent: "Al2(SO4)3", ions: [["Al3+", 2], ["SO4(2-)", 3]], class: "salt" },
    { parent: "Pb(NO3)2", ions: [["Pb2+", 1], ["NO3-", 2]], class: "salt" },
    { parent: "Pb(CH3COO)2", ions: [["Pb2+", 1], ["CH3COO-", 2]], class: "salt" },
    { parent: "MnSO4", ions: [["Mn2+", 1], ["SO4(2-)", 1]], class: "salt" },
    { parent: "LiCl", ions: [["Li+", 1], ["Cl-", 1]], class: "salt" }
];
/** Quick index for `findDominantParent`. */
const PARENT_BY_FORMULA = (() => {
    const m = new Map();
    for (const def of DISSOCIATION_PARENTS)
        m.set(def.parent, def);
    return m;
})();
export function getDissociationDef(formula) {
    return PARENT_BY_FORMULA.get(formula);
}
/**
 * Identify the parent compound that best explains a set of dissolved
 * ions. A parent matches when **every** ion it produces is present
 * above `minConcentration` mol/L. When several parents match, prefer
 * the most specific one (most ions, then highest stoichiometry sum).
 *
 * Returns the matched parent or null if no parent's ion set is fully
 * represented. Used by the bottle-label regenerator to recover the
 * acid/base name from an ions-only mixture.
 */
export function findDominantParent(ions, minConcentration = 1e-3) {
    const present = new Set();
    for (const [f, c] of ions) {
        if (c >= minConcentration)
            present.add(f);
    }
    let best = null;
    let bestScore = -1;
    for (const def of DISSOCIATION_PARENTS) {
        const ionFormulas = def.ions.map(([f]) => f);
        if (!ionFormulas.every((f) => present.has(f)))
            continue;
        // Score: count of ions × 10 + sum of stoichiometric coefficients.
        // Favours specific multi-ion compounds (H2SO4: 2 ions, Σ=3 ⇒ 23)
        // over single-pair ones (HCl: 2 ions, Σ=2 ⇒ 22) and over generic
        // single-ion entries (none currently, but the score generalises).
        const score = ionFormulas.length * 10
            + def.ions.reduce((s, [, n]) => s + n, 0);
        if (score > bestScore) {
            bestScore = score;
            best = def;
        }
    }
    return best;
}
